import React, { useState, useEffect, useRef } from 'react';
import { BarChart3, Receipt, Users, MessageSquare, Mic, Bot, Lock, X, Activity, Send, Phone, Loader2, Check, AlertCircle, TrendingUp, Unlink, BrainCircuit, Menu } from 'lucide-react';
import EmployeeManagement from './pos/EmployeeManagement';
import ExpenseManagement from './pos/ExpenseManagement';
import FinanceDashboard from './pos/FinanceDashboard';
import LunaMemoryView from './pos/LunaMemoryView';

interface MasterDashboardProps {
  children: React.ReactNode;
  onClose: () => void;
  storeName: string;
  storeApiKey: string;
  isReadOnly?: boolean;
}

const sidebarTabLabels = [
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'finance',   label: 'Finance & P&L', icon: TrendingUp },
  { id: 'expenses',  label: 'Expenses & Bills', icon: Receipt },
  { id: 'employees', label: 'Employee Management', icon: Users },
  { id: 'luna-memory', label: 'Luna Memory', icon: BrainCircuit },
  { id: 'voice',     label: 'Luna AI Voice', icon: Mic },
] as const;

type SidebarTab = typeof sidebarTabLabels[number]['id'];

const Placeholder = ({ title }: { title: string }) => (
  <div className="flex flex-col items-center justify-center h-full text-slate-400">
    <Activity size={48} className="mb-4 opacity-30" />
    <p className="text-[15px] font-bold text-slate-500">{title}</p>
    <p className="text-[11px] text-slate-400 mt-1">Coming in Phase 2</p>
  </div>
);

const LunaVoiceBillingTab: React.FC<{ storeApiKey: string; storeName: string; isReadOnly?: boolean }> = ({ storeApiKey, storeName, isReadOnly = false }) => {
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [saved, setSaved] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [storeId, setStoreId] = useState<string | null>(null);
  const autoSaveRef = useRef<ReturnType<typeof setTimeout>>();

  // Auto-save phone number after 1.5s of inactivity
  useEffect(() => {
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    if (!phone.trim() || !storeId || saved) return;
    autoSaveRef.current = setTimeout(() => { saveNumber(); }, 1500);
    return () => { if (autoSaveRef.current) clearTimeout(autoSaveRef.current); };
  }, [phone]);
  const [telegramChatId, setTelegramChatId] = useState<string | null>(null);
  const [botPlatform, setBotPlatform] = useState<string>('telegram');
  const [unlinking, setUnlinking] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  // Expiry notification settings
  const [expiryNotifyEnabled, setExpiryNotifyEnabled] = useState(true);
  const [expiryNotifyDaysBefore, setExpiryNotifyDaysBefore] = useState(1);
  const [savingExpiry, setSavingExpiry] = useState(false);

  // ── Load store data, honoring the isUnlinking flag ──
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch('/api/my-stores', { headers: { 'x-store-api-key': storeApiKey } });
        if (!res.ok) return;
        const stores = await res.json();
        const s = stores.find((st: any) => st.apiKey === storeApiKey);
        if (s) {
          setStoreId(String(s.id));
          // 🛡️ Smart unlink: only force-disconnect if backend ALSO confirms no number
          // If the store still has managerWhatsapp, the flag is stale — clear it
          const justUnlinked = localStorage.getItem('kangavena:isUnlinking') === 'true';
          if (justUnlinked && !s.managerWhatsapp && !s.phone) {
            setPhone('');
            setBotPlatform('none');
          } else {
            setPhone(s.managerWhatsapp || s.phone || '');
            setBotPlatform(s.botPlatform || 'telegram');
            // Stale flag — store actually has a number, so clear it
            if (justUnlinked) localStorage.removeItem('kangavena:isUnlinking');
          }
          setTelegramChatId(s.telegramChatId || null);
          setExpiryNotifyEnabled(s.expiryNotifyEnabled !== false && s.expiryNotifyEnabled !== 0);
          setExpiryNotifyDaysBefore(s.expiryNotifyDaysBefore || 1);
        }
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    void load();
  }, [storeApiKey]);

  // ── Auto-clear isUnlinking flag after 15s (survives page reload) ──
  useEffect(() => {
    if (localStorage.getItem('kangavena:isUnlinking') === 'true') {
      const t = setTimeout(() => localStorage.removeItem('kangavena:isUnlinking'), 15000);
      return () => clearTimeout(t);
    }
  }, []);

  // ── Save botPlatform preference ──
  const saveBotPlatform = async (platform: string) => {
    if (!storeId) return;
    setBotPlatform(platform);
    setError(null);
    try {
      const token = localStorage.getItem('kangavena:cashierToken') || '';
      await fetch(`/api/stores/${storeId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-store-api-key': storeApiKey, ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({ botPlatform: platform }),
      });
    } catch { /* silent */ }
  };

  // ── Save expiry notification settings ──
  const saveExpirySettings = async (updates: { expiryNotifyEnabled?: boolean; expiryNotifyDaysBefore?: number }) => {
    if (!storeId || isReadOnly) return;
    setSavingExpiry(true);
    try {
      const token = localStorage.getItem('kangavena:cashierToken') || '';
      await fetch(`/api/stores/${storeId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-store-api-key': storeApiKey, ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify(updates),
      });
    } catch { /* silent */ }
    finally { setSavingExpiry(false); }
  };

  // Poll for Telegram link status when not linked
  useEffect(() => {
    if (telegramChatId) { setIsPolling(false); return; }
    setIsPolling(true);
    const id = setInterval(async () => {
      try {
        const res = await fetch('/api/my-stores', { headers: { 'x-store-api-key': storeApiKey } });
        if (!res.ok) return;
        const stores = await res.json();
        const s = stores.find((st: any) => st.apiKey === storeApiKey);
        if (s?.telegramChatId) setTelegramChatId(s.telegramChatId);
      } catch { /* silent */ }
    }, 3000);
    return () => { clearInterval(id); setIsPolling(false); };
  }, [telegramChatId, storeApiKey]);

  const unlinkTelegram = async () => {
    setUnlinking(true); setError(null);
    try {
      const token = localStorage.getItem('kangavena:cashierToken') || '';
      const res = await fetch('/api/telegram/unlink', {
        method: 'PUT',
        headers: { 'x-store-api-key': storeApiKey, ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unlink failed');
      setTelegramChatId(null);
    } catch (e: any) { setError(e.message); }
    finally { setUnlinking(false); }
  };

  const saveNumber = async () => {
    if (!storeId || !phone.trim()) return;
    setSaving(true); setError(null); setSaved(false);
    try {
      const token = localStorage.getItem('kangavena:cashierToken') || '';
      const res = await fetch(`/api/stores/${storeId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-store-api-key': storeApiKey, ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({ managerWhatsapp: phone.trim(), registeredPhone: phone.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!phone.trim()) { setError('Save a WhatsApp number first'); return; }
    setSending(true); setError(null); setSent(false);
    try {
      const token = localStorage.getItem('kangavena:cashierToken') || '';
      const res = await fetch('/api/reports/day-end/send-whatsapp', {
        method: 'POST',
        headers: { 'x-store-api-key': storeApiKey, ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Send failed');
      setSent(true);
      setTimeout(() => setSent(false), 4000);
    } catch (e: any) { setError(e.message); }
    finally { setSending(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400">
        <Loader2 size={24} className="animate-spin" />
      </div>
    );
  }

  const isWhatsAppLinked = !!(phone && phone.trim().length >= 10);
  const platforms = [
    { id: 'telegram', label: 'Telegram', icon: Bot, color: 'sky', linked: !!telegramChatId },
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare, color: 'emerald', linked: isWhatsAppLinked },
    { id: 'none',     label: 'Disabled', icon: Mic, color: 'slate', linked: false },
  ] as const;

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <div className="shrink-0 flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-white/80 backdrop-blur-md">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-sky-400 to-blue-500 shadow-lg shadow-sky-200 flex items-center justify-center">
            <Mic size={15} className="text-white" />
          </div>
          <div>
            <h2 className="text-[15px] font-black text-slate-800">Luna AI Voice</h2>
            <p className="text-[10px] text-slate-500">Select your voice billing platform</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">

        {/* ── DEFAULT CHAT PLATFORM SELECTOR ── */}
        <div>
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2.5">Default Chat Platform</p>
          <div className="grid grid-cols-3 gap-2.5">
            {platforms.map(p => {
              const Icon = p.icon;
              const isActive = botPlatform === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => saveBotPlatform(p.id)}
                  disabled={isReadOnly}
                  className={`flex flex-col items-center gap-1.5 py-3.5 px-2 rounded-2xl border-2 transition-all duration-200 ${
                    isReadOnly ? 'opacity-70 cursor-not-allowed' : ''
                  } ${
                    isActive
                      ? `border-${p.color}-500 bg-${p.color}-50 shadow-md shadow-${p.color}-200/50`
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isActive ? `bg-${p.color}-500 text-white shadow-md` : `bg-slate-100 text-slate-500`}`}>
                    <Icon size={20} />
                  </div>
                  <span className={`text-[11px] font-black ${isActive ? `text-${p.color}-700` : 'text-slate-500'}`}>{p.label}</span>
                  {p.linked && <span className={`w-1.5 h-1.5 rounded-full bg-${p.color}-500`} />}
                </button>
              );
            })}
          </div>
          <p className="text-[9px] text-slate-400 mt-1.5 text-center">
            {botPlatform === 'telegram' ? 'Luna responds via Telegram Bot. Voice notes, chat, and billing.' :
             botPlatform === 'whatsapp' ? 'Luna responds via WhatsApp. Voice notes, chat, and billing.' :
             'Voice billing is disabled. No platform selected.'}
          </p>
        </div>

        {/* ⚡ 1-Click WhatsApp Link — only active after saving managerWhatsapp */}
        {phone && phone.trim().length >= 10 ? (
          <a
            href={`https://wa.me/${'94788114888'}?text=/link%20${storeApiKey}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-2xl bg-[#25D366] hover:bg-[#128C7E] text-white text-[12px] font-black uppercase tracking-widest transition-all active:scale-[0.97] shadow-lg shadow-green-500/30"
          >
            <MessageSquare size={16} />
            ⚡ 1-Click Connect via WhatsApp
          </a>
        ) : (
          <div className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-2xl bg-slate-100 border border-slate-200 text-slate-400 text-[12px] font-black uppercase tracking-widest cursor-not-allowed">
            <MessageSquare size={16} />
            Save your WhatsApp number below to unlock 1-Click Connect
          </div>
        )}

        {/* ── TELEGRAM SECTION ── */}
        {botPlatform === 'telegram' && (
        <div className={`rounded-2xl border-2 shadow-sm overflow-hidden transition-all duration-200 ${botPlatform === 'telegram' ? 'border-sky-300 bg-white' : 'bg-white/70 border-slate-200'}`}>
          <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-sky-50 to-blue-50">
            <Bot size={18} className="text-sky-600" />
            <span className="text-[13px] font-black text-slate-800">Telegram Bot</span>
            <span className="ml-auto px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[8px] font-black uppercase tracking-wider">Free</span>
          </div>
          <div className="p-4 space-y-3">
            {telegramChatId ? (
              <>
                <div className="flex flex-col items-center gap-3 py-4 text-center">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Check size={28} className="text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-[14px] font-black text-emerald-800">Linked to Telegram</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">Send voice notes to @Kangavena_bot — Luna will respond instantly.</p>
                  </div>
                </div>
                {!isReadOnly && (
                  <button onClick={unlinkTelegram} disabled={unlinking}
                    className="shrink-0 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-rose-50 text-slate-500 hover:text-rose-600 font-bold text-[11px] uppercase tracking-wider transition-colors disabled:opacity-50 border border-transparent hover:border-rose-200">
                    {unlinking ? <Loader2 size={13} className="animate-spin" /> : <Unlink size={13} />}
                    Unlink
                  </button>
                )}
              </>
            ) : (
              <>
                <p className="text-[11px] text-slate-600">Scan the QR code or open Telegram to link your store.</p>
                <div className="flex justify-center">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://t.me/Kangavena_bot?start=${storeApiKey}`}
                    alt="Telegram QR" className="rounded-xl shadow-sm border border-slate-200"
                  />
                </div>
                {isPolling && (
                  <div className="flex items-center justify-center gap-2.5 py-2">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
                      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-500" />
                    </span>
                    <span className="text-[11px] text-amber-700 font-semibold tracking-wide">Waiting for connection...</span>
                  </div>
                )}
                <a href={`https://t.me/Kangavena_bot?start=${storeApiKey}`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl bg-sky-500 hover:bg-sky-600 text-white text-[12px] font-black uppercase tracking-widest transition-all active:scale-[0.97] shadow-lg shadow-sky-200">
                  <Bot size={15} /> Open in Telegram
                </a>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-[9px] text-slate-400 font-medium uppercase tracking-wider">or manual</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                <p className="text-[10px] text-slate-500">Search <span className="font-bold text-sky-700">@Kangavena_bot</span> and send:</p>
                <div className="px-3 py-2.5 rounded-xl bg-slate-900 text-emerald-300 font-mono text-[11px] select-all tracking-wide shadow-inner">
                  /link {storeApiKey}
                </div>
              </>
            )}
          </div>
        </div>
        )}

        {/* ── WHATSAPP SECTION ── */}
        {botPlatform === 'whatsapp' && (
        <div className="rounded-2xl border-2 border-emerald-300 bg-white shadow-sm overflow-hidden transition-all duration-200">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-emerald-50 to-green-50">
            <MessageSquare size={18} className="text-emerald-600" />
            <span className="text-[13px] font-black text-slate-800">WhatsApp</span>
          </div>
          <div className="p-4 space-y-3">
            {isWhatsAppLinked ? (
              <>
                <div className="flex flex-col items-center gap-3 py-4 text-center">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Check size={28} className="text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-[14px] font-black text-emerald-800">WhatsApp Connected</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      Voice orders & day-end reports go to <span className="font-bold text-emerald-700">{phone}</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-2 text-[10px] text-slate-400 mb-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Listening for voice orders
                </div>
                {/* Change / Unlink buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => { setPhone(''); setSaved(false); }}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-sky-50 hover:bg-sky-100 border border-sky-200 text-sky-700 text-[11px] font-bold transition-all active:scale-[0.97]"
                  >
                    <Phone size={12} /> Change Number
                  </button>
                  {!isReadOnly && (
                    <button
                      onClick={async () => {
                        // 🛡️ Set unlink flag BEFORE the fetch — survives page reload
                        localStorage.setItem('kangavena:isUnlinking', 'true');
                        setUnlinking(true); setError(null);
                        try {
                          const token = localStorage.getItem('kangavena:cashierToken') || '';
                          const res = await fetch('/api/whatsapp/unlink', {
                            method: 'PUT',
                            headers: { 'x-store-api-key': storeApiKey, ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
                          });
                          if (!res.ok) {
                            const data = await res.json().catch(() => ({}));
                            throw new Error(data.error || 'Unlink failed');
                          }
                          // Clear local state immediately so the reload finds clean state
                          setPhone('');
                          setBotPlatform('none');
                          window.location.reload();
                        } catch (e: any) {
                          setError(e.message);
                          localStorage.removeItem('kangavena:isUnlinking');
                        }
                        finally { setUnlinking(false); }
                      }}
                      disabled={unlinking}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 text-[11px] font-bold transition-all active:scale-[0.97] disabled:opacity-40"
                    >
                      {unlinking ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
                      Unlink
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <button onClick={sendTest} disabled={sending || !phone.trim()}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-bold uppercase tracking-wider disabled:opacity-40 transition-all active:scale-[0.97]">
                    {sending ? <Loader2 size={13} className="animate-spin" /> : sent ? <Check size={13} /> : <Send size={13} />}
                    {sending ? 'Sending...' : sent ? 'Sent!' : 'Send Test Day-End Report'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-[11px] text-slate-600">
                  Enter your WhatsApp number to receive day-end reports and use Luna Voice Billing.
                </p>
                {error && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-[11px] font-medium">
                    <AlertCircle size={13} className="shrink-0" />{error}
                  </div>
                )}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">WhatsApp Number</label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white border border-slate-200 focus-within:border-emerald-400 transition-colors">
                      <Phone size={14} className="text-slate-400 shrink-0" />
                      <input type="tel" value={phone}
                        onChange={e => { setPhone(e.target.value); setError(null); setSaved(false); }}
                        disabled={isReadOnly}
                        placeholder="+947XXXXXXXX" className="flex-1 text-[13px] font-mono text-slate-800 bg-transparent outline-none placeholder:text-slate-400 disabled:opacity-50" />
                    </div>
                    {!isReadOnly && (
                      <button onClick={saveNumber} disabled={saving || !phone.trim()}
                        className="shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-bold uppercase tracking-wider disabled:opacity-40 transition-all active:scale-[0.97]">
                        {saving ? <Loader2 size={13} className="animate-spin" /> : saved ? <Check size={13} /> : 'Save'}
                      </button>
                    )}
                  </div>
                  <p className="text-[9px] text-slate-400">Include country code. Example: +947****4567</p>
                </div>
                {!isReadOnly && (
                  <button onClick={sendTest} disabled={sending || !phone.trim()}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white text-[12px] font-black uppercase tracking-widest disabled:opacity-40 transition-all active:scale-[0.97]">
                    {sending ? <Loader2 size={14} className="animate-spin" /> : sent ? <Check size={14} /> : <Send size={14} />}
                    {sending ? 'Sending...' : sent ? 'Sent!' : 'Send Test Day-End Report'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        )}

        {/* ── DISABLED SECTION ── */}
        {botPlatform === 'none' && (
        <div className="rounded-2xl bg-white/70 backdrop-blur-sm border border-slate-200 shadow-sm overflow-hidden">
          <div className="flex flex-col items-center gap-3 py-10 px-4 text-center">
            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center">
              <Mic size={28} className="text-slate-400" />
            </div>
            <div>
              <p className="text-[14px] font-black text-slate-400">Voice Billing is Disabled</p>
              <p className="text-[10px] text-slate-400 mt-0.5 max-w-xs">
                Select Telegram or WhatsApp above to activate Luna AI Voice Billing for your store.
              </p>
            </div>
          </div>
        </div>
        )}

        {/* ── EXPIRY NOTIFICATION SETTINGS ── */}
        <div className="rounded-2xl border-2 border-amber-300 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-amber-50 to-orange-50">
            <div className="w-8 h-8 rounded-xl bg-amber-500 flex items-center justify-center">
              <span className="text-white text-[14px]">⏰</span>
            </div>
            <div>
              <span className="text-[13px] font-black text-slate-800">Expiry Notifications</span>
              <p className="text-[9px] text-slate-500">Alert before products expire</p>
            </div>
            {savingExpiry && <Loader2 size={14} className="animate-spin text-amber-500 ml-auto" />}
          </div>
          <div className="p-4 space-y-4">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[12px] font-bold text-slate-700">Expiry Reminders</p>
                <p className="text-[9px] text-slate-500">Get notified when stock items are about to expire</p>
              </div>
              <button
                onClick={() => {
                  const newVal = !expiryNotifyEnabled;
                  setExpiryNotifyEnabled(newVal);
                  saveExpirySettings({ expiryNotifyEnabled: newVal });
                }}
                disabled={isReadOnly}
                className={`relative w-12 h-7 rounded-full transition-all duration-300 ${
                  expiryNotifyEnabled
                    ? 'bg-gradient-to-r from-amber-400 to-orange-500 shadow-md shadow-amber-200'
                    : 'bg-slate-200'
                } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <div className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-all duration-300 ${
                  expiryNotifyEnabled ? 'left-[calc(100%-1.625rem)]' : 'left-0.5'
                }`} />
              </button>
            </div>

            {/* Days Before Setting */}
            {expiryNotifyEnabled && (
              <div className="space-y-2">
                <p className="text-[12px] font-bold text-slate-700">Notify Before</p>
                <div className="grid grid-cols-4 gap-2">
                  {[1, 2, 3, 7].map(days => (
                    <button
                      key={days}
                      onClick={() => {
                        setExpiryNotifyDaysBefore(days);
                        saveExpirySettings({ expiryNotifyDaysBefore: days });
                      }}
                      disabled={isReadOnly}
                      className={`py-2.5 rounded-xl text-[12px] font-black transition-all ${
                        expiryNotifyDaysBefore === days
                          ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-md shadow-amber-200'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {days === 1 ? '1 Day' : `${days} Days`}
                    </button>
                  ))}
                </div>
                <p className="text-[9px] text-slate-400">
                  Luna will send an alert {expiryNotifyDaysBefore} day{expiryNotifyDaysBefore > 1 ? 's' : ''} before products expire
                </p>
              </div>
            )}

            {/* Status Info */}
            {expiryNotifyEnabled && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-amber-50 border border-amber-200">
                <span className="text-[14px]">✅</span>
                <div>
                  <p className="text-[11px] font-bold text-amber-800">Active</p>
                  <p className="text-[9px] text-amber-600">
                    Daily check at 8:00 AM • Alerts via {botPlatform === 'whatsapp' ? 'WhatsApp' : botPlatform === 'telegram' ? 'Telegram' : 'linked platform'}
                  </p>
                </div>
              </div>
            )}

            {!expiryNotifyEnabled && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200">
                <span className="text-[14px]">⏸️</span>
                <div>
                  <p className="text-[11px] font-bold text-slate-500">Disabled</p>
                  <p className="text-[9px] text-slate-400">Expiry notifications are turned off for this store</p>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default function MasterDashboard({ children, onClose, storeName, storeApiKey, isReadOnly = false }: MasterDashboardProps) {
  const [tab, setTab] = useState<SidebarTab>('analytics');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  return (
    <div className="fixed inset-0 z-[9995] flex bg-slate-50">
      
      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/40 z-[9996] md:hidden backdrop-blur-sm transition-opacity" 
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed md:relative z-[9997] w-[250px] shrink-0 bg-white border-r border-sky-100 flex flex-col h-full transform transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
        {/* Brand header */}
        <div className="px-5 py-5 border-b border-sky-100 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 shadow-lg shadow-sky-200 flex items-center justify-center">
                <Activity size={15} className="text-white" />
              </div>
              <h2 className="text-[14px] font-bold text-slate-800 tracking-tight">ERP Dashboard</h2>
            </div>
            <p className="text-[10px] text-slate-500 font-medium truncate">{storeName}</p>
          </div>
          {/* Close button inside sidebar for mobile */}
          <button onClick={() => setIsSidebarOpen(false)} className="md:hidden p-2 bg-slate-50 text-slate-400 hover:text-slate-700 rounded-lg">
            <X size={16} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {sidebarTabLabels.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => {
                  setTab(t.id);
                  setIsSidebarOpen(false); // Close sidebar on mobile after clicking
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-[11px] font-semibold transition-all ${
                  active
                    ? 'bg-sky-50 text-sky-600 border border-sky-200 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50 border border-transparent'
                }`}
              >
                <Icon size={15} />
                <span>{t.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Close button */}
        <div className="p-3 border-t border-sky-100">
          <button
            onClick={onClose}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-[11px] font-bold text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-all border border-transparent hover:border-slate-200"
          >
            <X size={14} />
            Close Dashboard
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden bg-slate-50/50 w-full relative">
        {/* Mobile Header Bar */}
        <div className="md:hidden shrink-0 flex items-center justify-between px-4 py-3 bg-white border-b border-sky-100 sticky top-0 z-50 shadow-sm">
          <div className="flex items-center gap-2">
            <button onClick={() => setIsSidebarOpen(true)} className="p-2 -ml-2 bg-slate-50 text-slate-600 hover:text-slate-800 rounded-xl transition-colors">
              <Menu size={18} />
            </button>
            <h1 className="text-[13px] font-black text-slate-800 uppercase tracking-widest">{sidebarTabLabels.find(t => t.id === tab)?.label}</h1>
          </div>
          <button onClick={onClose} className="p-2 -mr-2 bg-rose-50 hover:bg-rose-100 text-rose-500 rounded-xl transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {tab === 'analytics' ? (
            children
          ) : tab === 'finance' ? (
            <FinanceDashboard storeApiKey={storeApiKey} isReadOnly={isReadOnly} />
          ) : tab === 'expenses' ? (
            <ExpenseManagement storeApiKey={storeApiKey} isReadOnly={isReadOnly} />
          ) : tab === 'employees' ? (
            <EmployeeManagement storeApiKey={storeApiKey} isReadOnly={isReadOnly} />
          ) : tab === 'luna-memory' ? (
            <LunaMemoryView storeApiKey={storeApiKey} />
          ) : tab === 'voice' ? (
            <LunaVoiceBillingTab storeApiKey={storeApiKey} storeName={storeName} isReadOnly={isReadOnly} />
          ) : null}
        </div>
      </main>
    </div>
  );
}
